import { useMoneyFormatter } from "@/lib/money-format";
import { useEffect, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Ban, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { TREASURY_STATUS_BADGE_VARIANT } from "@/components/treasury/format";
import {
  useCashReceipt,
  useVoidCashReceipt,
} from "@/lib/api/cash-receipts.queries";
import { translateApiError } from "@/lib/translate-api-error";
import { useActiveCompany } from "@/lib/company-context";
import { DocumentJournalPanel } from "@/components/journal/DocumentJournalPanel";

export const Route = createFileRoute(
  "/companies/$companyId/recibos/$receiptId",
)({
  component: CashReceiptDetailPage,
});

function CashReceiptDetailPage() {
  return (
    <RequireAuth>
      <AppShell>
        <CashReceiptDetailContent />
      </AppShell>
    </RequireAuth>
  );
}

function CashReceiptDetailContent() {
  const formatCop = useMoneyFormatter();
  const { t } = useTranslation();
  const { companyId, receiptId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const detailQuery = useCashReceipt(companyId, receiptId);
  const voidMutation = useVoidCashReceipt(companyId);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState("");

  if (detailQuery.isPending) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (detailQuery.isError || !detailQuery.data) {
    return (
      <ErrorState
        title={t("cashReceipts.detail.error_title")}
        description={t("cashReceipts.detail.error_body")}
        error={detailQuery.error ?? undefined}
        onRetry={() => void detailQuery.refetch()}
        retryLabel={t("common.retry")}
      />
    );
  }

  const receipt = detailQuery.data;

  async function handleVoid() {
    if (!voidReason.trim()) {
      toast.error(t("treasury.void.reason_required"));
      return;
    }
    try {
      await voidMutation.mutateAsync({ receiptId, reason: voidReason.trim() });
      toast.success(t("cashReceipts.toast.voided"));
      setVoidOpen(false);
      setVoidReason("");
    } catch (err) {
      toast.error(translateApiError(err, t, "cashReceipts.error"));
    }
  }

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/companies/$companyId/recibos"
            params={{ companyId }}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" />
            {t("cashReceipts.detail.back")}
          </Link>
          <div className="mt-1 flex flex-wrap items-center gap-3">
            <h1 className="font-mono text-2xl font-semibold tracking-tight">
              RC-{receipt.receiptNumber}
            </h1>
            <Badge variant={TREASURY_STATUS_BADGE_VARIANT[receipt.status]}>
              {t(`treasury.status.${receipt.status}`)}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {receipt.receiptDate} ·{" "}
            {t(`treasury.method.${receipt.paymentMethod}`)}
            {receipt.reference ? ` · ${receipt.reference}` : ""}
          </p>
        </div>
        {receipt.status === "posted" ? (
          <Button
            variant="destructive"
            onClick={() => setVoidOpen(true)}
            disabled={voidMutation.isPending}
          >
            <Ban className="h-4 w-4" />
            {t("cashReceipts.actions.void")}
          </Button>
        ) : null}
      </div>

      {receipt.status === "void" && receipt.voidReason ? (
        <Card className="border-destructive/40">
          <CardContent className="pt-6 text-sm">
            <span className="font-medium">
              {t("cashReceipts.detail.void_reason")}:
            </span>{" "}
            {receipt.voidReason}
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t("cashReceipts.detail.amounts")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1 text-sm">
            <Row
              label={t("cashReceipts.balance.received")}
              value={formatCop(receipt.amountReceived)}
            />
            {receipt.retefuenteAmount > 0 ? (
              <Row
                label={t("cashReceipts.form.retefuente")}
                value={formatCop(receipt.retefuenteAmount)}
              />
            ) : null}
            {receipt.reteivaAmount > 0 ? (
              <Row
                label={t("cashReceipts.form.reteiva")}
                value={formatCop(receipt.reteivaAmount)}
              />
            ) : null}
            {receipt.reteicaAmount > 0 ? (
              <Row
                label={t("cashReceipts.form.reteica")}
                value={formatCop(receipt.reteicaAmount)}
              />
            ) : null}
            {receipt.discountAmount > 0 ? (
              <Row
                label={t("cashReceipts.form.discount")}
                value={formatCop(receipt.discountAmount)}
              />
            ) : null}
            <div className="border-t pt-2">
              <Row
                label={t("cashReceipts.balance.total_to_apply")}
                value={formatCop(receipt.totalApplied)}
                bold
              />
            </div>
            {receipt.journalEntryId ? (
              <Link
                to="/companies/$companyId/contabilidad/diario/$entryId"
                params={{ companyId, entryId: receipt.journalEntryId }}
                className="inline-block pt-2 text-xs text-primary hover:underline"
              >
                {t("cashReceipts.detail.journal_link")}
              </Link>
            ) : null}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t("cashReceipts.detail.applications")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("cashReceipts.detail.invoice")}</TableHead>
                  <TableHead className="text-right">
                    {t("cashReceipts.form.applied_amount")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {receipt.applications.map((application) => (
                  <TableRow key={application.id}>
                    <TableCell className="font-mono text-sm">
                      {application.invoiceDocumentId ?? application.invoiceId}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {formatCop(application.amount)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {receipt.notes ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {t("cashReceipts.detail.notes")}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm">{receipt.notes}</CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("journal.document.title")}
          </CardTitle>
          <CardDescription>{t("journal.document.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent>
          <DocumentJournalPanel
            companyId={companyId}
            sourceTable="cash_receipts"
            documentId={receiptId}
          />
        </CardContent>
      </Card>

      <Dialog open={voidOpen} onOpenChange={setVoidOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("cashReceipts.void.title")}</DialogTitle>
            <DialogDescription>{t("cashReceipts.void.body")}</DialogDescription>
          </DialogHeader>
          <div>
            <Label htmlFor="receipt-void-reason">
              {t("treasury.void.reason")}
            </Label>
            <textarea
              id="receipt-void-reason"
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              rows={2}
              className="block w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder={t("treasury.void.reason_placeholder")}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoidOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleVoid()}
              disabled={voidMutation.isPending || !voidReason.trim()}
            >
              {t("cashReceipts.void.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({
  label,
  value,
  bold,
}: {
  label: string;
  value: string;
  bold?: boolean;
}) {
  return (
    <div
      className={`flex items-center justify-between ${bold ? "font-semibold" : ""}`}
    >
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}
